import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { socialService } from '../services/socialService';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../hooks/use-toast';

const FacebookCallbackPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const hasProcessed = useRef(false); // Use a ref to prevent double processing
    const queryClient = useQueryClient();
    const { toast } = useToast();

    useEffect(() => {
        console.log("--- FACEBOOK CALLBACK PAGE TRACE ---");
        console.log("1. Component mounted, parsing URL...");
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        console.log(`2. Parsed URL params: code=${code}, state=${state}`);

        // Prevent double processing on strict mode re-renders or accidental reloads
        if (hasProcessed.current) {
            console.log("Already processed this callback. Navigating away.");
            navigate('/app/dashboard', { replace: true });
            return;
        }

        if (code && state) {
            hasProcessed.current = true; // Mark as processed
            
            // Clean the URL to prevent re-submission on refresh/back button
            window.history.replaceState({}, document.title, location.pathname);

            console.log("3. Code and state found. Calling handleFacebookCallback service...");
            socialService.handleFacebookCallback(code, state)
                .then((response) => {
                    console.log("4. Service call successful. Response:", response);
                    
                    // Show success toast
                    toast({
                        title: 'Facebook Connected!',
                        description: 'Successfully connected your Facebook account. Refresh the page to see your analytics.',
                        variant: 'default',
                    });
                    
                    console.log("5. Navigating to dashboard...");
                    navigate('/app/dashboard', { replace: true });
                })
                .catch((error) => {
                    console.error('4. Service call FAILED. Error:', error);
                    if (error.response) {
                        console.error('Error response data:', error.response.data);
                    }
                    
                    // Show error toast
                    toast({
                        title: 'Facebook Connection Failed',
                        description: 'Failed to connect your Facebook account. Please try again.',
                        variant: 'destructive',
                    });
                    
                    console.log("5. Navigating to dashboard due to error...");
                    navigate('/app/dashboard', { replace: true });
                });
        } else {
            console.error('3. No code or state found in callback URL. Navigating...');
            navigate('/app/dashboard', { replace: true });
        }
    }, [location, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50 dark:from-gray-900 dark:to-slate-900">
            <div className="text-center">
                <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Connecting to Facebook</h2>
                <p className="text-gray-600 dark:text-gray-400">Please wait while we set up your Facebook connection...</p>
            </div>
        </div>
    );
};

export default FacebookCallbackPage;
